import {
  select, call, put, spawn,
} from 'redux-saga/effects';
import { CQ_ACTION_CODES, CQ_POTS, NETFLIX_PO } from '../catalogConstants';
import {
  initCQ as initCQBit,
  extendCQ as extendCQBit,
} from '../bitSagas/initNExtendCQ';
import {
  initNExtendCQTV,
  extendCQTV as extendCQTVBit
} from '../bitSagas/initNExtendCQTV';
import {
  initCQAfterSale as initCQAfterSaleBit,
} from '../bitSagas/initCQAfterSale';
import {
  extendCQAfterSale as extendCQAfterSaleBit,
} from '../bitSagas/extendCQAfterSale';
import {
  selectElements as selectElementsBit,
} from '../bitSagas/selectElements';
import {
  getFeatures as getFeaturesBit,
} from '../bitSagas/getFeatures';
import {
  addToCart as addToCartBit,
} from '../bitSagas/addToCart';
import {
  getPOTs as getPOTsBit,
} from '../bitSagas/getPOTs';
import {
  validatePin as validatePinBit,
} from '../bitSagas/pin';
import {
  updateCA as updateCABit,
} from '../bitSagas/updateCA';
import { actions as loaderControlActions } from '../reducers/loaderControl';
import { actions as uiNeedsActions } from '../reducers/uiNeeds';
import { actions as taggingActions } from '../reducers/tagging';
import { actions as summaryActions } from '../reducers/summaryInfo';
import { actions as apisNeedsActions } from '../reducers/apisNeeds';
import { actions as offerActions } from '../reducers/offer';
import { commonActions } from '../reducers/common';
import { getCQPrices, getProvisionalAgreement } from './cq';
import { MOBILE_INSURANCE } from '../bitConstants/constants';
import {
  normalizeSecondarySelectionArray,
  getOfferingCharacteristics,
  hasCharacteristicsToResolve,
  isAccountWithRiskError,
  isPpipAvailable,
  normalizePortabilityInfo,
  normalizeSelectElementsSummaryChoices,
  normalizeSelectElementsChoices
} from './normalizers/ascertain';
import { megaMinderStatus } from '../minder/MegaMinder/megaMinderStatus';
import { actions as localizedStringsActions } from '../reducers/localizedStrings';
import { getSpecificLocalizedStringsObj, STRING_KEYS } from '../localizedStrings';
import { normalizeShoppingCartPrices } from './normalizers/prices';
import { isFundingPayment } from '../utils/payment';
import { isTherePermanentResidenceStep } from './normalizers/flow';
import { generateAliasSellingProduct, normalizeSelectElementsTaggingChoices, replaceCommaByDot } from '../tagging';
import { sendRetargeting } from './retargeting';
import { areThereProductOfferingTypesIntoOfferArray, intersection, removeDuplicates } from '../utils/general';
import { PRODUCT_OFFERING_TYPES, PRODUCT_TYPES } from '../utils/catalog';
import { FLOW_TYPES } from '../flowDefinition/flowTypes';

const apisNeedsStore = state => state.apiNeeds;
const offerStore = state => state.offer;
const flowStore = state => state.flow;
const contextStore = state => state.context.data;
const getErrorManagement = state => state.errorManagement;
const localizedStringsStore = state => state.localizedStrings;
const manualStore = state => state.manual;

/**
 * Función generadora que se encarga de realizar la llamada al API
 * actualizar/cuenta (updateCA).
 * @param {obj} params Objeto con los parámetros de manejo de errores y elecciones del cliente
 * <pre>
 *            - showError: acción de redux para mostrar una pantalla de error
 *            - normalizeHttpErrors: método que normaliza el error para poder ser mostrado en UX
 * </pre>
 */
export function* updateCA(input) {
  const apiNeeds = yield select(apisNeedsStore);
  const { cqId, accountInfo } = apiNeeds;
  const { showError, normalizeHttpErrors } = input;
  const reduxUpdate = {
    successUpdate: null,
    errorUpdate: showError,
  };

  const updateCAParams = {
    cqId,
    reduxUpdate,
    showError,
    normalizeHttpErrors,
    customerAccount: accountInfo,
  };

  const { localizedStrings } = yield select(localizedStringsStore);
  yield put(
    loaderControlActions.setLoaderState({
      loading: true,
      loadingText: localizedStrings[STRING_KEYS.waitingPageTexts.updateCA],
    }),
  );
  try {
    yield call(updateCABit, updateCAParams);
    return yield call(getCQPrices, input);
  } catch (error) {
    console.log(error);
    if (isAccountWithRiskError(error)) {
      let riskError = null;
      if (error?.response?.status === 'KO'
        && error?.response?.error?.codigoErrorNegocio
      ) {
        riskError = error.response.error.codigoErrorNegocio;
      }
      yield put(uiNeedsActions.setAccountWithRisk(riskError));
    }

    const errorResponse = megaMinderStatus.KO;
    errorResponse.errorInfo = error;

    return errorResponse;
  }
}

/**
 * Función generadora que se encarga de realizar la llamada al API
 * obtener/medios (getPOTs).
 * @param {obj} params Objeto con los parámetros de manejo de errores y elecciones del cliente
 * <pre>
 *            - showError: acción de redux para mostrar una pantalla de error
 *            - normalizeHttpErrors: método que normaliza el error para poder ser mostrado en UX
 * </pre>
 */
export function* getPOTs(input) {
  const apiNeeds = yield select(apisNeedsStore);
  const { flowJson } = yield select(flowStore);
  const offer = yield select(offerStore);

  const { cqId, myProductsInfo, currentDate } = apiNeeds;
  const { poit: poitReceived } = offer;

  const { showError, normalizeHttpErrors } = input;
  const reduxUpdate = {
    successUpdate: uiNeedsActions.setUpdateCQNeeds,
    errorUpdate: showError,
  };

  
  let shippingStepAllowed = true;
  let packAddress;
  if (flowJson.minProductAgeToChangeAddress) {
    packAddress = myProductsInfo.newAddressesArray.find(address => address.productType === PRODUCT_TYPES.PACK)
    if(currentDate && packAddress?.startDate){
      
      let requiredDate = new Date();
      requiredDate.setDate(currentDate.getDate()-flowJson.minProductAgeToChangeAddress);
      let productDate = new Date(packAddress.startDate);
      if (requiredDate > productDate) {
        shippingStepAllowed = true
      } else if (requiredDate <= productDate) {
        shippingStepAllowed = false;
      }
    } else {
      shippingStepAllowed = false;
    }
  }

  const getPOTsParams = {
    cqId,
    shippingStepAllowed,
    reduxUpdate,
    normalizeHttpErrors,
  };

  const { localizedStrings } = yield select(localizedStringsStore);
  yield put(
    loaderControlActions.setLoaderState({
      loading: true,
      loadingText: localizedStrings[STRING_KEYS.waitingPageTexts.getPOTs],
    }),
  );
  
  let response = null;
  try {
    response = yield call(getPOTsBit, getPOTsParams);
  } catch (e) {
    return null;
  }
  if (response && response.choices && Array.isArray(response.choices)) {
    const posht = response.choices.find(
      choice => choice.type === CQ_POTS.SHIPPING_POT.TYPE,
    );

    const poit = response.choices.find(
      choice => choice.type === CQ_POTS.INSTALLATION_POT.TYPE,
    );

    const popt = response.choices.find(
      choice => choice.type === CQ_POTS.PAYMENT_POT.TYPE,
    );

    let homeShippingToResolve = null;
    if (posht?.terms && Array.isArray(posht.terms)) {
      homeShippingToResolve = posht.terms.find((term) => {
        return term.id === CQ_POTS.SHIPPING_POT.HOME_SHIPPING_ID;
      });
    }

    const poitToResolve = poit?.terms && Array.isArray(poit.terms) && poit.terms.length > 1;

    let autoresolvePoitSelection = false;
    if(poitToResolve && poitReceived){
      const poitFound = poit.terms.find((term) => {
        return term.id === poitReceived;
      });
      if(poitFound){
        autoresolvePoitSelection = true;
      }
    }


    if (popt?.terms && Array.isArray(popt.terms) && popt.terms.length === 1) {
      yield put(commonActions.setPoptSelected(popt.terms[0]));
    }

    if (flowJson?.autoSelectPoshtByCharacSelected?.id) {
      const { addToCartNeeds } = apiNeeds;

      if (addToCartNeeds?.chosenActionsArray && Array.isArray(addToCartNeeds.chosenActionsArray)) {
        let poshtSelected = null;

        addToCartNeeds.chosenActionsArray.forEach(chosenActionArray => {
          if (chosenActionArray?.offeringCharacteristics && Array.isArray(chosenActionArray.offeringCharacteristics)) {
            chosenActionArray.offeringCharacteristics.forEach(offeringCharacteristic => {
              if (offeringCharacteristic.id === flowJson.autoSelectPoshtByCharacSelected.id) {
                const { value } = offeringCharacteristic;

                if (flowJson?.autoSelectPoshtByCharacSelected?.selections?.[value] &&
                  flowJson?.autoSelectPoshtByCharacSelected?.selections?.[value].length === 1
                ) {
                  poshtSelected = flowJson.autoSelectPoshtByCharacSelected.selections[value][0];
                }
              }
            })
          }
        });
        
        if (poshtSelected) {
          yield put(commonActions.setPoshtSelected(poshtSelected));
          if (poshtSelected !== CQ_POTS.SHIPPING_POT.HOME_SHIPPING_ID) {
            homeShippingToResolve = null;
          }
        }
      }
    }

    // NUEVO

    if (homeShippingToResolve) {
      if (!shippingStepAllowed) {
        yield put(commonActions.setAddressInfo(packAddress));
      } else if (!flowJson.autoSelectHomeShippingAddress) {
        yield put(commonActions.setPoshtSelected(homeShippingToResolve.id));
      }
    }

    if (!homeShippingToResolve && posht?.terms?.length === 1) {
      yield put(commonActions.setPoshtSelected(posht.terms[0].id));
    }

    if(!poitToResolve && poit?.terms?.length === 1) {
      yield put(commonActions.setPoitSelected(poit.terms[0]));
    } else if(poitToResolve && autoresolvePoitSelection){
      yield put(commonActions.setPoitSelected({id: poitReceived}));
    }

    const getProvisionalAgreementParams = {
      showError,
      normalizeHttpErrors,
    };

    if (
      !homeShippingToResolve &&
      (
        !poitToResolve ||
        (poitToResolve && autoresolvePoitSelection)
      )
    ) {
      yield call(getProvisionalAgreement, getProvisionalAgreementParams);
    } else if (
      homeShippingToResolve &&
      (
        !shippingStepAllowed ||
        flowJson.autoSelectHomeShippingAddress ||
        (
          poitToResolve &&
          poitReceived &&
          flowJson.sendInstallationAddressIfInstallerSelected &&
          poitReceived === CQ_POTS.INSTALLATION_POT.INSTALLATION_ID
        )
      )
    ) {
      yield call(getProvisionalAgreement, getProvisionalAgreementParams);
    }
  }
  return null;
}

/**
 * Función generadora que se encarga de realizar la llamada al API
 * agregar/alcarro (addToCart).
 * @param {obj} params Objeto con los parámetros de manejo de errores y elecciones del cliente
 * <pre>
 *            - showError: acción de redux para mostrar una pantalla de error
 *            - normalizeHttpErrors: método que normaliza el error para poder ser mostrado en UX
 *            - chosenActionsArray: array de elecciones llevadas a cabo por el cliente
 * </pre>
 */
export function* addToCart(input) {
  const apiNeeds = yield select(apisNeedsStore);
  const flowInfo = yield select(flowStore);

  const { cqId, ppipInfo, addToCartNeeds } = apiNeeds;
  let { chosenActionsArray } = addToCartNeeds;

  const { showError, normalizeHttpErrors } = input;
  const reduxUpdate = {
    successUpdate: null,
    errorUpdate: showError,
  };

  // TODO Revisar MIMO
  if (ppipInfo && ppipInfo.clientPhoneNumberToBeAppliedTo && ppipInfo.id) { // TODO deberia guardarse en apiNeeds directamente ya en el array no?
    if (chosenActionsArray) {
      chosenActionsArray.push({ ...ppipInfo });
    } else {
      chosenActionsArray = [{ ...ppipInfo }];
    }
  }

  let showPrices = false;

  if (flowInfo
    && flowInfo.flowJson
    && flowInfo.flowJson.showAddToCartPrices
  ) {
    showPrices = true;
  }

  const addToCartParams = {
    cqId,
    chosenActionsArray,
    reduxUpdate,
    normalizeHttpErrors,
    showPrices,
  };

  const { localizedStrings } = yield select(localizedStringsStore);
  yield put(
    loaderControlActions.setLoaderState({
      loading: true,
      loadingText: localizedStrings[STRING_KEYS.waitingPageTexts.addToCart],
    }),
  );
  let productRequest = null;
  try {
    const response = yield call(addToCartBit, addToCartParams);
    if (response && addToCartParams.showPrices) {
      const responseNormalized = yield call(normalizeShoppingCartPrices, response.prices.components);
      yield put(uiNeedsActions.setPricesInfo(responseNormalized));
      sessionStorage.setItem('pricesInfo', JSON.stringify(responseNormalized));
    }
    if (response.productRequest) {
      productRequest = response.productRequest;
    }
  } catch (e) {
    console.log('ERROR', e);
    return null;
  }

  const offerInfo = yield select(offerStore);
  const {
    secondaryProductOfferings,
    productOfferingsToUpgrade,
    productOfferingsToDowngrade,
    secondaryR2Rs,
    ottProductsArray,
  } = offerInfo;
  
  const nextApiParams = {
    showError,
    normalizeHttpErrors,
  };

  if (secondaryProductOfferings
    && Array.isArray(secondaryProductOfferings)
    && secondaryProductOfferings.length > 0
  ) {
    const { cisi, netflixObj } = yield select(offerStore);
    if (flowInfo.flowJson.isAddonFlow && netflixObj?.productOffering && secondaryProductOfferings.some(po => po === netflixObj.productOffering)) {

      if (cisi && netflixObj?.mode) {
        const { netflixPoId } = apiNeeds.myProductsInfo;
    
        if (netflixPoId) {
          yield put(commonActions.setAfterSaleParentProduct({
            id: netflixPoId,
          }));
        }
    
        yield call(extendCQAfterSale, {...input, productOfferingId: netflixObj.productOffering});
    
        return megaMinderStatus.OK;
      }
    } else if(Array.isArray(ottProductsArray) && ottProductsArray.length > 0){
      if(apiNeeds.myProductsInfo?.tvProducts && apiNeeds.myProductsInfo.tvProducts[ottProductsArray[0].productOffering]){
        yield put(commonActions.setAfterSaleParentProduct({
          id: apiNeeds.myProductsInfo.tvProducts[ottProductsArray[0].productOffering],
        }));
        
        yield call(extendCQAfterSale, {...input, productOfferingId: ottProductsArray[0].productOffering});
    
        return megaMinderStatus.OK;
      } else {
        yield call(extendCQ, {...nextApiParams, productOfferingId: ottProductsArray[0].productOffering});
      }
    } else {
      yield call(extendCQ, nextApiParams);
    }
  } else if(productOfferingsToUpgrade || productOfferingsToDowngrade){
    if (productRequest?.productOfferingsToActivate?.length > 0 || productRequest?.productsToDeactivate?.length > 0) {
      nextApiParams.productOfferingsToUpgrade = null;
      nextApiParams.productOfferingsToDowngrade = null;

      if (productRequest?.productOfferingsToActivate?.length > 0) {
        nextApiParams.productOfferingsToUpgrade = [];

        productRequest.productOfferingsToActivate.forEach(poId => {
          if (poId?.id && productOfferingsToUpgrade.indexOf(poId.id) !== -1) {
            nextApiParams.productOfferingsToUpgrade.push(poId.id);
          }
        })
      }

      if (productRequest?.productsToDeactivate?.length > 0) {
        if (productOfferingsToDowngrade) {
          nextApiParams.productOfferingsToDowngrade = [];
          if (apiNeeds.myProductsInfo?.tvProducts) {
            productOfferingsToDowngrade.forEach(poId => {
              if (productRequest.productsToDeactivate.some(productToDeactivate => productToDeactivate.id === apiNeeds.myProductsInfo.tvProducts[poId])) {
                nextApiParams.productOfferingsToDowngrade.push(poId);
              }
            })
          }
        }
      }

      yield call(extendCQTV, nextApiParams);
    } else if (flowInfo.flowJson.isAddonFlow) {
      nextApiParams.productOfferingsToUpgrade = productOfferingsToUpgrade;
      nextApiParams.productOfferingsToDowngrade = productOfferingsToDowngrade;

      yield call(extendCQTV, nextApiParams);
    } else {
      yield call(getPOTs, nextApiParams);
    }
  } else if(secondaryR2Rs
    && Array.isArray(secondaryR2Rs)
    && secondaryR2Rs.length > 0
  ) {
    const nextR2RToBuy = secondaryR2Rs.shift();

    const nextR2RToBuyParts = nextR2RToBuy.split('_');

    if (nextR2RToBuyParts.length !== 3) {
      yield call(showError, 0);
    }

    const nextPO = nextR2RToBuyParts[0];
    const nextStorageId = nextR2RToBuyParts[1];
    const nextColorId = nextR2RToBuyParts[2];

    yield put(apisNeedsActions.setNewColorAndStorage({
      storageId: nextStorageId,
      colorId: nextColorId,
    }))

    yield put(offerActions.setNewSecondaryR2R(secondaryR2Rs));

    yield put(offerActions.setNewSecondaryProductOfferings([nextPO]));
    yield call(extendCQ, nextApiParams);
  } else {
    yield call(getPOTs, nextApiParams);
  }

  return null;
}

/**
 * Función generadora que se encarga de realizar la llamada al API
 * obtener/características (getFeatures). Si el array de components tiene
 * 1 o menos posiciones se puede llamar al API agregar/alCarro (addToCart)
 * @param {obj} params Objeto con los parámetros de manejo de errores y elecciones del cliente
 * <pre>
 *            - showError: acción de redux para mostrar una pantalla de error
 *            - normalizeHttpErrors: método que normaliza el error para poder ser mostrado en UX
 *            - chosenActionsArray: array de elecciones llevadas a cabo por el cliente
 * </pre>
 */
export function* getFeatures(input) {
  const apiNeeds = yield select(apisNeedsStore);
  const {
    cqId,
    deviceInfo,
    clientInfo,
    getFeaturesNeeds,
  } = apiNeeds;

  const { multiSim } = yield select(manualStore);

  const offerInfo = yield select(offerStore);
  const {
    actualProductOffering, netflixObj, lmasProductOfferings, secondaryProductOfferings, swap, quotaId, speedId, ottProductsArray,
  } = offerInfo;
  const { showError, normalizeHttpErrors } = input;
  const reduxUpdate = {
    successUpdate: null,
    errorUpdate: showError,
  };

  let chosenActionsArray = null;

  if (getFeaturesNeeds) {
    if (getFeaturesNeeds.chosenActions
      && getFeaturesNeeds.chosenActions.length > 0
    ) {
      chosenActionsArray = getFeaturesNeeds.chosenActions;
    }
    if (getFeaturesNeeds.productOfferingsSelected
      && getFeaturesNeeds.productOfferingsSelected.length > 0
      && lmasProductOfferings
      && lmasProductOfferings.length > 0
    ) {
      const poIntersection = intersection(lmasProductOfferings, getFeaturesNeeds.productOfferingsSelected);
      chosenActionsArray = getFeaturesNeeds.chosenActions;
      if (secondaryProductOfferings
        && secondaryProductOfferings.length > 0
        && poIntersection
        && poIntersection.length > 0
      ) {
        const newSecondaryProductOfferings = removeDuplicates(poIntersection, secondaryProductOfferings);

        yield put(offerActions.setNewSecondaryProductOfferings(newSecondaryProductOfferings));
      }
    }
  }


  const getFeaturesParams = {
    cqId,
    chosenActionsArray,
    reduxUpdate,
    normalizeHttpErrors,
  };

  const { localizedStrings } = yield select(localizedStringsStore);
  yield put(
    loaderControlActions.setLoaderState({
      loading: true,
      loadingText: localizedStrings[STRING_KEYS.waitingPageTexts.getFeatures],
    }),
  );
  let response = null;
  try {
    response = yield call(getFeaturesBit, getFeaturesParams);
  } catch (e) {
    return null;
  }

  const { colorId, storageId } = deviceInfo;

  let userMail = null;

  if (clientInfo && clientInfo.contactEmail && clientInfo.contactEmail.value) {
    userMail = clientInfo.contactEmail.value;
  }

  let netflixMode = null;

  if (netflixObj
    && netflixObj.productOffering
    && netflixObj.mode){
    netflixMode = netflixObj.mode;
  }
  const flowInfo = yield select(flowStore);
  const { flowJson } = flowInfo;

  const { actionsResolved, tvMailAdded, netflixConfigured, r2rInfo, speedUnavailable } = getOfferingCharacteristics({
    components: response.components,
    colorId,
    storageId,
    mail: userMail,
    netflixMode,
    simTypeToAutoSelect: flowJson.simTypeToAutoSelect,
    multiSim,
    speedId,
    ottProductsArray,
  });

  if(speedUnavailable){
    const { handledErrors } = yield select(getErrorManagement);
    yield put(showError(handledErrors.BUSINESS_ERRORS.SPEED_UNAVAILABLE));
    return null;
  }

  if (r2rInfo) {
    yield put(offerActions.setActualR2RInfo(r2rInfo));
  }

  if(netflixConfigured && netflixObj && netflixObj.productOffering && netflixObj.productOffering !== actualProductOffering){
    const newSecondaryProductOfferings = secondaryProductOfferings.filter(secondaryProductOffering => secondaryProductOffering !== netflixObj.productOffering);
    yield put(offerActions.setNewSecondaryProductOfferings(newSecondaryProductOfferings));
  }
  // TODO SWAP - DELETE
  let swapPOC;
  let preselectedQuota = false;
  if(( flowJson.flowName === FLOW_TYPES.R2R
    || flowJson.flowName === FLOW_TYPES.PACK_R2R
    || flowJson.flowName === FLOW_TYPES.R2R_AUTO_RENEWAL
    || flowJson.flowName === FLOW_TYPES.R2R_2 )
    && deviceInfo.swap && swap){
    response.components.forEach((component) =>{
      const { customerQuotePossibleActions, productOfferingTypes } = component;
      if (
        Array.isArray(customerQuotePossibleActions) &&
        customerQuotePossibleActions.length === 1 &&
        areThereProductOfferingTypesIntoOfferArray(productOfferingTypes, PRODUCT_OFFERING_TYPES.SWAP)
      ){
        const { offeringTerms } = customerQuotePossibleActions[0];
        if (Array.isArray(offeringTerms) && offeringTerms.length > 1) {
          swapPOC = offeringTerms.find(term => term.id === CQ_POTS.COMMITMENT_POT["36_MONTHS"]);
          customerQuotePossibleActions[0].offeringTerms = [swapPOC];
        }
      }
    })
  } else if (flowJson.quotaPreselected) {
    response.components.forEach((component) =>{
      const { customerQuotePossibleActions } = component;
      if (
        Array.isArray(customerQuotePossibleActions) &&
        customerQuotePossibleActions.length === 1
      ){
        const { offeringTerms } = customerQuotePossibleActions[0];
        if (Array.isArray(offeringTerms) && offeringTerms.length > 1) {
          preselectedQuota = offeringTerms.find(term => term.id === flowJson.quotaPreselected);
          customerQuotePossibleActions[0].offeringTerms = [preselectedQuota];
        }
      }
    })
  }

  yield put(commonActions.setAddToCartNeeds({ response, actionsResolved }));
  if(swapPOC){
    yield put(apisNeedsActions.setCommitmentSelected(swapPOC.id));
  } else if (preselectedQuota) {
    yield put(apisNeedsActions.setCommitmentSelected(preselectedQuota.id));
  }
  yield put(summaryActions.setTvMailAdded(tvMailAdded));
  const characteristicWithPpip = isPpipAvailable(response.components);
  if (characteristicWithPpip) {
    yield put(commonActions.setPpipAsAvailable(characteristicWithPpip));
  } else if (
    response.components
    && !hasCharacteristicsToResolve(response.components)
  ) {
    yield call(addToCart, { showError, normalizeHttpErrors });
  } else if (quotaId && flowJson.allowQuotaIdPreselect) {
    // TODO resolver term de cuota
    yield put(apisNeedsActions.setCommitmentSelected(quotaId));
    yield call(addToCart, { showError, normalizeHttpErrors });
  }

  return null;
}

/**
 * Función generadora que se encarga de realizar la llamada al API
 * seleccionar/elementos (selectElements). Si las customerQuoteItems
 * que se reciben son menores o iguales a 1 se pasa a llamar al API
 * obtener/características (getFeatures)
 * @param {obj} params Objeto con los parámetros de manejo de errores
 * <pre>
 *            - showError: acción de redux para mostrar una pantalla de error
 *            - normalizeHttpErrors: método que normaliza el error para poder ser mostrado en UX
 * </pre>
 */
export function* selectElements(params) {
  const { showError, normalizeHttpErrors, isExtendCQ } = params;
  const reduxUpdate = {
    successUpdate: commonActions.setAlreadyOwnedProductsChoices,
    errorUpdate: showError,
  };
  const { extendSelectElementsNeeds: manualExtendSelectElementsNeeds } = yield select(manualStore);
  const { lmasProductOfferings, netflixObj } = yield select(offerStore);

  let apiNeeds = yield select(apisNeedsStore);
  let { selectElementsNeeds: selectElementsNeedsRedux, selectElementsOptions } = apiNeeds;
  const { cqId } = apiNeeds;

  let selectElementsNeeds = JSON.parse(JSON.stringify(selectElementsNeedsRedux));

  if(manualExtendSelectElementsNeeds && isExtendCQ){
    if(manualExtendSelectElementsNeeds?.mainOptionsObj?.mainOptionSelected){

      const { mainOptionSelected: actionId, phoneNumber } = manualExtendSelectElementsNeeds.mainOptionsObj;
      const { cqsiId, productType, description, productOfferingName } = selectElementsOptions.find(item => item.actionId === actionId);

      selectElementsNeeds = {
        actionId,
        cqsiId,
        productType,
        productOfferingName,
        description,
        selectedByClient: true,
        phoneNumber: phoneNumber || null
      };
    }
  }
  // Obetenemos tanto el userType del cliente como el flujo en el que está navegando
  const { userType } = yield select(contextStore);
  const { flowJson } = yield select(flowStore);
  let flowId = null;
  if (flowJson && flowJson.flowName) {
    flowId = flowJson.flowName;
  }
  
  let { actionId, cqsiId, phoneNumber } = selectElementsNeeds;
  let chosenActionsArray = normalizeSecondarySelectionArray(selectElementsNeeds.secondarySelection);

  // Obtenemos los textos localizados a userType, flujo y palanca principal
  const localizedStrings = getSpecificLocalizedStringsObj(
    flowId,
    userType,
    actionId,
    flowJson.isSelfEmployedFlow,
  );
  yield put(localizedStringsActions.setLocalizedStrings(localizedStrings));

  const input = {
    cqId,
    cqsiId,
    phoneNumber,
    chosenActionsArray,
    reduxUpdate,
    normalizeHttpErrors,
    productOfferingsArray: lmasProductOfferings,
    netflixObj,
  };
  const selectElementsTaggingChoices = normalizeSelectElementsTaggingChoices(selectElementsNeeds);
  yield put(taggingActions.setSelectElementsChoices(selectElementsTaggingChoices));

  const portabilityInfo = normalizePortabilityInfo(selectElementsNeeds);
  yield put(apisNeedsActions.setPortabilityInfo(portabilityInfo));

  const selectElementsSummaryChoices = normalizeSelectElementsSummaryChoices(selectElementsNeeds);
  yield put(summaryActions.setChoices(selectElementsSummaryChoices));

  const selectElementsChoices = normalizeSelectElementsChoices(selectElementsNeeds);
  yield put(apisNeedsActions.setChoices(selectElementsChoices));
  
  yield put(
    loaderControlActions.setLoaderState({
      loading: true,
      loadingText: localizedStrings[STRING_KEYS.waitingPageTexts.selectElements],
    }),
  );
  let response = null;
  try {
    response = yield call(selectElementsBit, input);
  } catch (e) {
    return null;
  }

  if (response.areLMAChoices) {
    if (lmasProductOfferings && Array.isArray(lmasProductOfferings) && lmasProductOfferings.length > 0) {
      const title = localizedStrings[STRING_KEYS.mobileSecondaryAction.lmaStepAvice.title];
      const steps = localizedStrings[STRING_KEYS.mobileSecondaryAction.lmaStepAvice.steps];
      if (title) {
        yield put(uiNeedsActions.setShowStepAdvice({
          adviceTitle: title,
          steps,
        }));
      }
    }
  }

  if (!response.mustAskClient) {
    yield call(getFeatures, { showError, normalizeHttpErrors });
  }
  return null;
}


/**
 * Función generadora que se encarga de realizar la llamada al API
 * extender/cq (extendCQ) para iniciar una nueva vuelta de carro.
 * @param {obj} params Objeto con los parámetros de manejo de errores y elecciones del cliente
 * <pre>
 *            - showError: acción de redux para mostrar una pantalla de error
 *            - normalizeHttpErrors: método que normaliza el error para poder ser mostrado en UX
 * </pre>
 */
export function* extendCQ(input) {
  // TODO: revisar pantalla de carga
  const {
    showError,
    normalizeHttpErrors,
    productOfferingId,
  } = input;

  const apisNeedsInfo = yield select(apisNeedsStore);
  const { cqId, deviceInfo, portabilityOTPNeeded, myProductsInfo } = apisNeedsInfo;

  const flowInfo = yield select(flowStore);

  const { flowJson } = flowInfo;


  const offerInfo = yield select(offerStore);
  const { secondaryProductOfferings, ottProductsArray } = offerInfo;

  let newSecondaryProductOfferings = [...secondaryProductOfferings];

  let secondaryProductOffering = null;
  if(productOfferingId){
    secondaryProductOffering=productOfferingId;
    newSecondaryProductOfferings = newSecondaryProductOfferings.filter(secondaryProductOffering => secondaryProductOffering !== productOfferingId);
    const newOTTProducts = ottProductsArray.filter(ottProduct => ottProduct.productOffering !== productOfferingId);
    yield put(offerActions.saveOTTProductsInfo(newOTTProducts));
  } else {
    secondaryProductOffering = newSecondaryProductOfferings.shift();
  }

  yield put(offerActions.setNewSecondaryProductOfferings(newSecondaryProductOfferings));
  yield put(offerActions.setActualProductOffering(secondaryProductOffering));
  yield put(commonActions.resetAscertain());

  const {extendSelectElementsNeeds: manualExtendSelectElementsNeeds } = yield select(manualStore);

  const { linesMobile } = myProductsInfo;
  let olderPackMobileLine;

  if(Array.isArray(linesMobile) && linesMobile.length > 1){
    const packLines = linesMobile.filter(line=>line.isPack);
    if(packLines.length > 0){
      olderPackMobileLine = packLines[0];
    } else {
      olderPackMobileLine = linesMobile[0];
    }
  } else if(Array.isArray(linesMobile) && linesMobile.length > 0) {
    olderPackMobileLine = linesMobile[0];
  }
  
  let extendCQParams = {
    normalizeHttpErrors,
    reduxUpdate: {
      errorUpdate: showError,
      successUpdate: commonActions.setSelectElementsNeeds,
    },
    productOffering: secondaryProductOffering,
    cqId,
    preSelectedOptions: manualExtendSelectElementsNeeds,
    
  };

  if(flowJson?.sendOlderPackMobileLineToExtendCQ && olderPackMobileLine){
    extendCQParams.productIdDepend = olderPackMobileLine.productId;
  }

  const { localizedStrings } = yield select(localizedStringsStore);
  yield put(
    loaderControlActions.setLoaderState({
      loading: true,
      loadingText: localizedStrings[STRING_KEYS.waitingPageTexts.extendCQ],
    }),
  );
  const response = yield call(extendCQBit, extendCQParams);

  // TODO: revisar con carinio

  let { productOfferingName } = response;
  productOfferingName = replaceCommaByDot(productOfferingName);

  if (deviceInfo && deviceInfo.mobileInsurance && deviceInfo.mobileInsurance.modality !== MOBILE_INSURANCE.NS_OPTION && deviceInfo.mobileInsurance.name) {
    productOfferingName = `${productOfferingName},${deviceInfo.mobileInsurance.name}`;
  }
  sessionStorage.setItem('aliasSellingProduct', productOfferingName);

  const { multiPromo, needToAskForSecondaryOptions } = response;

  if (multiPromo) {
    const { handledErrors } = yield select(getErrorManagement);
    yield put(showError(handledErrors.BUSINESS_ERRORS.COMPATIBLE_PROMOTIONS));
  } else if (response.mainOptionSelected || (manualExtendSelectElementsNeeds && !portabilityOTPNeeded)) { 
    if (!needToAskForSecondaryOptions || (manualExtendSelectElementsNeeds && !portabilityOTPNeeded)) {
      if(manualExtendSelectElementsNeeds?.mainOptionsObj?.mainOptionSelected === CQ_ACTION_CODES.PORTABILITY && manualExtendSelectElementsNeeds?.mainOptionsObj?.phoneNumber && !portabilityOTPNeeded){
        yield call(validatePinBit, { cqId, phoneNumber: manualExtendSelectElementsNeeds.mainOptionsObj.phoneNumber, showError, normalizeHttpErrors });
      }
      yield call(selectElements, { isExtendCQ: true, showError, normalizeHttpErrors });
    } else {
      yield put(
        loaderControlActions.setLoaderState({
          loading: false,
        }),
      );
    }
  } else {
    // TODO Aqui queda trabajo

    yield put(
      loaderControlActions.setLoaderState({
        loading: false,
      }),
    );
  }
}

export function* initCQ(params) {
  const apiNeeds = yield select(apisNeedsStore);
  const flowInfo = yield select(flowStore);
  const { allPOsToBuy, bonus } = yield select(offerStore);
  const { lmaBuyProcessUrl, lmaName, lmaPrice, bafName } = yield select(manualStore);

  const { flowJson } = flowInfo;
  const { isSelfEmployedFlow, useReceivedAlias } = flowJson;

  const {
    parentProductIdSelected,
    installationAddress,
    originalPermanentResidenceInfo,
    updatePermanentResidenceNeeds,
    deviceInfo,
    myProductsInfo,
    productOfferingsToBuyInfo,
  } = apiNeeds;

  if ((!originalPermanentResidenceInfo || !originalPermanentResidenceInfo.id)
  && (!updatePermanentResidenceNeeds || !updatePermanentResidenceNeeds.id)
  ) {
    if (installationAddress
      && flowJson
      && flowJson.steps
      && isTherePermanentResidenceStep(flowJson.steps)
    ) {
      return megaMinderStatus.OK;
    }
  }

  const offerInfo = yield select(offerStore);

  let { parentProductId } = offerInfo;

  const {
    productOffering,
    productOfferingAlt,
    subType,
    promotion,
    actualProductOffering,
    productOfferingsToUpgrade,
    productOfferingsToDowngrade,
  } = offerInfo;

  let { productId } = offerInfo;

  if (apiNeeds.cqId) {
    return null;
  }

  const { showError, normalizeHttpErrors } = params;

  const reduxUpdate = {
    successUpdate: commonActions.setSelectElementsNeeds,
    errorUpdate: showError,
  };

  if (parentProductIdSelected) {
    parentProductId = parentProductIdSelected;
  }

  const { handledErrors } = yield select(getErrorManagement);
  const { localizedStrings } = yield select(localizedStringsStore);
  const incompatibleProductsErrorToHandle = localizedStrings[STRING_KEYS.controlledErrors.incompatibleProducts];

  if (flowJson.autoSelectProductId
    && myProductsInfo
    && myProductsInfo.clientCategory
    && myProductsInfo.clientCategory.products
  ) {
    const product = myProductsInfo.clientCategory.products.find(element => element.productType === flowJson.autoSelectProductId);

    if (product) {
      yield put(uiNeedsActions.setShowInstallationStep(false));
      if (myProductsInfo.newAddressesArray
        && Array.isArray(myProductsInfo.newAddressesArray)
        && myProductsInfo.newAddressesArray.length >= 1
      ) {
        const productAddress = myProductsInfo.newAddressesArray.find(address => address.productId === product.productId);
        if(productAddress){
          yield put(commonActions.setInstallationAddress(productAddress));
        }
      }
      ({ productId } = product);
    } else if (!installationAddress || !installationAddress.locationId) {
      yield put(uiNeedsActions.setShowInstallationStep(true));
      return megaMinderStatus.OK;
    }
  }

  let allowNoActionOptions = true;
  if(flowJson.blockNoActionIfBonusSelected && bonus){
    allowNoActionOptions = false;
  }

  if (flowJson.blockNoAction) {
    allowNoActionOptions = false;
  }

  const initCQparams = {
    productOfferings: [productOffering, productOfferingAlt],
    actualProductOffering,
    allPOsToBuy,
    parentProductId,
    subType,
    promotion,
    productId,
    reduxUpdate,
    normalizeHttpErrors,
    showError,
    errorCoverageObj: handledErrors.BUSINESS_ERRORS.WITHOUT_COVERAGE,
    incompatibleProductsErrorToHandle,
    allowNoActionOptions,
    forcedInitialFirstLevelActionIfNecessary: flowJson.forcedInitialFirstLevelActionIfNecessary || null,
    forcedLandLineActionIfNecessary: flowJson.forcedLandLineActionIfNecessary || null,
    buyProductOfferingAltWhenErrors: flowJson.buyProductOfferingAltWhenErrors || null,
    saveErrorWhenProductOfferingChanged: uiNeedsActions.saveErrorWhenProductOfferingChanged,
  };

  if (flowJson && typeof flowJson.flowTypeTCOL !== 'undefined') {
    initCQparams.customerInquirySpec = flowJson.flowTypeTCOL;
    if (!productOfferingsToDowngrade && flowJson?.flowTypeTCOLWithoutDowngrades) {
      initCQparams.customerInquirySpec = flowJson.flowTypeTCOLWithoutDowngrades
      
      if (productOfferingsToUpgrade) {
        const newSecondaryProductOfferings = [...offerInfo.secondaryProductOfferings, ...productOfferingsToUpgrade];
        yield put(offerActions.cleanPOsToDowngradeNUpgrade());
        yield put(offerActions.setNewSecondaryProductOfferings(newSecondaryProductOfferings));
      }


    }
  }

  if (productOfferingsToUpgrade) {
    initCQparams.productOfferingsToActivate = productOfferingsToUpgrade;
  }

  if (productOfferingsToDowngrade) {
    let tvProductsToDowngrade = [];
      if (myProductsInfo?.tvProducts) {
        productOfferingsToDowngrade.forEach(poId => {
          if (myProductsInfo.tvProducts[poId]) {
            tvProductsToDowngrade.push(myProductsInfo.tvProducts[poId]);
          }
        })
  
        if (tvProductsToDowngrade.length > 0) {
          initCQparams.productsToDeactivate = tvProductsToDowngrade;
        }
      }
  }

  if (installationAddress && installationAddress.locationId) {
    initCQparams.locationId = installationAddress.locationId;
    if (
      installationAddress.productId &&
      (!flowJson.excludedAutoSelectProductIdRelatedToLocation ||
        (Array.isArray(flowJson.excludedAutoSelectProductIdRelatedToLocation) &&
          !flowJson.excludedAutoSelectProductIdRelatedToLocation.some(
            (excludedProductType) =>
              excludedProductType === installationAddress.productType
          )))
    ) {
      initCQparams.productId = installationAddress.productId;
    }
  }

  yield put(
    loaderControlActions.setLoaderState({
      loading: true,
      loadingText: localizedStrings[STRING_KEYS.waitingPageTexts.initCQ],
    }),
  );
  let response = null;
  try {
    response = yield call(initCQBit, initCQparams);
  } catch (error) {
    // TODO aqui tiene que ir la funcion normalizadora para sacar un mensaje u otro
    if (error
      && error.response
      && error.response.error
      && incompatibleProductsErrorToHandle
      && Array.isArray(incompatibleProductsErrorToHandle)
      && incompatibleProductsErrorToHandle.includes(error.response.error.codigoErrorNegocio)) {
      const apiParams = {
        reduxUpdate: {
          errorUpdate: showError,
          successUpdate: commonActions.setMyProductsInfo,
        },
      };
      try {
        response = yield call(getMyProductsBit, apiParams);
        const { clientCategory } = response;

        const errorDefaultClientTypes = localizedStrings[STRING_KEYS.controlledErrors.defaultClientTypes];

        if (errorDefaultClientTypes
          && Array.isArray(errorDefaultClientTypes)
          && errorDefaultClientTypes.includes(clientCategory.clientType)
        ) {
          yield put(showError(handledErrors.BUSINESS_ERRORS.INCOMPATIBLE_DEFAULT));
        } else {
          yield put(showError(handledErrors.BUSINESS_ERRORS.INCOMPATIBLE_EXCEPTION));
        }
      } catch (errorMyProducts) {
        yield put(showError(handledErrors.BUSINESS_ERRORS.INCOMPATIBLE_DEFAULT));
      }
    } else if(error?.response?.error?.codigoErrorNegocio && flowJson.redirectToAltBuyUrlWhenError && error.response.error.codigoErrorNegocio === flowJson.redirectToAltBuyUrlWhenError){
      const customError = handledErrors.BUSINESS_ERRORS.REDIRECT_TO_ALT_BUY_PROCESS_URL;
      customError.errorInfo.title = customError.errorInfo.title.replace("{bafName}",bafName);
      customError.errorInfo.msg[0] = customError.errorInfo.msg[0].replace("{lmaName}",lmaName);
      customError.errorInfo.msg[0] = customError.errorInfo.msg[0].replace("{lmaPrice}",lmaPrice);
      customError.errorInfo.buttons[0].link = lmaBuyProcessUrl;
      
      yield put(showError(customError));
    }
    return null;
  }
  const { cqId, nonFiberCoverage, newActualProductOffering, newAllPOsToBuy } = response;
  
  if(newActualProductOffering) {
    yield put(offerActions.setActualProductOffering(newActualProductOffering));
  }

  const { lmasProductOfferings } = offerInfo;

  if (lmasProductOfferings && Array.isArray(lmasProductOfferings) && lmasProductOfferings.length > 0) {
    const title = localizedStrings[STRING_KEYS.mobileSecondaryAction.lmaStepAvice.title];
    const steps = localizedStrings[STRING_KEYS.mobileSecondaryAction.lmaStepAvice.steps];
    if (title) {
      yield put(uiNeedsActions.setShowStepAdvice({
        adviceTitle: title,
        steps,
      }));
    }
  }

  let alias = generateAliasSellingProduct(productOfferingsToBuyInfo, newAllPOsToBuy, isSelfEmployedFlow);
  if (deviceInfo && deviceInfo.mobileInsurance && deviceInfo.mobileInsurance.modality !== MOBILE_INSURANCE.NS_OPTION && deviceInfo.mobileInsurance.name) {
    alias = `${alias},${deviceInfo.mobileInsurance.name}`;
  }
  if (alias && !useReceivedAlias) {
    yield put(taggingActions.setAliasSellingProduct(alias));
  }
  sessionStorage.setItem('aliasSellingProduct', alias);
  yield put(taggingActions.setCQ(cqId));
  yield put(commonActions.setNonFiberCoverage(nonFiberCoverage));

  const { multiPromo, needToAskForSecondaryOptions } = response;
  
  if (flowJson.retargeting
  ) {
    yield spawn(sendRetargeting, { showError, normalizeHttpErrors });
  }

  if (multiPromo) {
    yield put(showError(handledErrors.BUSINESS_ERRORS.COMPATIBLE_PROMOTIONS));
  } else if (response.mainOptionSelected) {
    if (!needToAskForSecondaryOptions) {
      yield call(selectElements, { showError, normalizeHttpErrors });
    } else {
      yield put(
        loaderControlActions.setLoaderState({
          loading: false,
        }),
      );
    }
  } else {
    // TODO Aqui queda trabajo
    yield put(
      loaderControlActions.setLoaderState({
        loading: false,
      }),
    );
  }

  return response;
}


export function* initCQAfterSale(params) {
  const apiNeeds = yield select(apisNeedsStore);
  const flowInfo = yield select(flowStore);
  const { cisi, netflixObj, secondaryProductOfferings, speedId, resourceSpecId, ottProductsArray, allPOsToBuy } = yield select(offerStore);


  const { flowJson } = flowInfo;

  const {
    afterSaleParentProduct,
    productOfferingsToBuyInfo,
  } = apiNeeds;

  if (apiNeeds.cqId) {
    return null;
  }

  const { showError, normalizeHttpErrors, productOfferingId } = params;

  const reduxUpdate = {
    successUpdate: apisNeedsActions.setCqId,
    errorUpdate: showError,
  };

  const { localizedStrings } = yield select(localizedStringsStore);
  const { handledErrors } = yield select(getErrorManagement);

  let afterSaleParentProductId = null;
  if (afterSaleParentProduct?.id) {
    afterSaleParentProductId = afterSaleParentProduct.id;
  }

  const initCQAfterSaleparams = {
    customerInquirySpecId: cisi,
    productId: afterSaleParentProductId,
    resourceSpecId,
    reduxUpdate,
    normalizeHttpErrors,
    showError,
  };

  yield put(
    loaderControlActions.setLoaderState({
      loading: true,
      loadingText: localizedStrings[STRING_KEYS.waitingPageTexts.initCQ],
    }),
  );
  let response = null;
  try {
    response = yield call(initCQAfterSaleBit, initCQAfterSaleparams);

    let alias = generateAliasSellingProduct(productOfferingsToBuyInfo, allPOsToBuy, flowJson.isSelfEmployedFlow);

    if (alias) {
      yield put(taggingActions.setAliasSellingProduct(alias));
    }

    if(response?.components){
      let netflixMode = null;
      
      if (netflixObj?.mode) {
        netflixMode = netflixObj.mode;
      }
      const { multiSim } = yield select(manualStore);
      const { actionsResolved, speedUnavailable, netflixConfigured } = getOfferingCharacteristics({
        components: response.components,
        simTypeToAutoSelect: flowJson.simTypeToAutoSelect,
        netflixMode,
        speedId,
        multiSim,
        ottProductsArray,
      });
      let newSecondaryProductOfferings = JSON.parse(JSON.stringify(secondaryProductOfferings));
      if (netflixConfigured && netflixObj?.productOffering) {
        newSecondaryProductOfferings = newSecondaryProductOfferings.filter(secondaryProductOffering => secondaryProductOffering !== netflixObj.productOffering);
        yield put(offerActions.setNewSecondaryProductOfferings(newSecondaryProductOfferings));
      }
      if(speedUnavailable){
        yield put(showError(handledErrors.BUSINESS_ERRORS.SPEED_UNAVAILABLE));
        return null;
      }
      if(productOfferingId){
        newSecondaryProductOfferings = newSecondaryProductOfferings.filter(secondaryProductOffering => secondaryProductOffering !== productOfferingId);
        yield put(offerActions.setNewSecondaryProductOfferings(newSecondaryProductOfferings));

        const newOTTProducts = ottProductsArray.filter(ottProduct => ottProduct.productOffering !== productOfferingId);
        yield put(offerActions.saveOTTProductsInfo(newOTTProducts));
      }
      yield put(commonActions.setAddToCartNeeds({ response, actionsResolved }));
    }
  
  } catch (error) {
    console.log(error);
    return null;
  }
  if (flowJson.retargeting
  ) {
    yield spawn(sendRetargeting, { showError, normalizeHttpErrors });
  }

  yield call(addToCart, { showError, normalizeHttpErrors });

  return response;
}

export function* extendCQAfterSale(params) {
  const apiNeeds = yield select(apisNeedsStore);
  const flowInfo = yield select(flowStore);
  const { cisi, netflixObj, secondaryProductOfferings, ottProductsArray } = yield select(offerStore);

  const { flowJson } = flowInfo;

  const {
    afterSaleParentProduct,
    cqId,
  } = apiNeeds;

  const { showError, normalizeHttpErrors, productOfferingId } = params;

  const reduxUpdate = {
    successUpdate: null,
    errorUpdate: showError,
  };

  const { localizedStrings } = yield select(localizedStringsStore);

  let afterSaleParentProductId = null;
  if (afterSaleParentProduct?.id) {
    afterSaleParentProductId = afterSaleParentProduct.id;
  }

  const extendCQAfterSaleParams = {
    customerInquirySpecId: cisi,
    productId: afterSaleParentProductId,
    cqId,
    reduxUpdate,
    normalizeHttpErrors,
    showError,
  };

  yield put(
    loaderControlActions.setLoaderState({
      loading: true,
      loadingText: localizedStrings[STRING_KEYS.waitingPageTexts.initCQ],
    }),
  );
  let response = null;
  try {
    response = yield call(extendCQAfterSaleBit, extendCQAfterSaleParams);

    if(response?.components){
      let netflixMode = null;
      
      if (netflixObj?.mode) {
        netflixMode = netflixObj.mode;
      }
      const { multiSim } = yield select(manualStore);
      const { actionsResolved } = getOfferingCharacteristics({
        components: response.components,
        simTypeToAutoSelect: flowJson.simTypeToAutoSelect,
        netflixMode,
        multiSim,
        ottProductsArray,
      });
      if(productOfferingId){
        const newSecondaryProductOfferings = secondaryProductOfferings.filter(secondaryProductOffering => secondaryProductOffering !== productOfferingId);
        yield put(offerActions.setNewSecondaryProductOfferings(newSecondaryProductOfferings));

        const newOTTProducts = ottProductsArray.filter(ottProduct => ottProduct.productOffering !== productOfferingId);
        yield put(offerActions.saveOTTProductsInfo(newOTTProducts));
      }
      yield put(commonActions.setAddToCartNeeds({ response, actionsResolved }));
    }
  
  } catch (error) {
    console.log(error);
    return null;
  }
  if (flowJson.retargeting
  ) {
    yield spawn(sendRetargeting, { showError, normalizeHttpErrors });
  }

  yield call(addToCart, { showError, normalizeHttpErrors });

  return response;
}

export function* initCQTV(params) {
  // TODO REVISAR
  const apiNeeds = yield select(apisNeedsStore);
  const flowInfo = yield select(flowStore);

  const { flowJson } = flowInfo;
  const { isSelfEmployedFlow } = flowJson;

  const {
    parentProductIdSelected,
    installationAddress,
    originalPermanentResidenceInfo,
    updatePermanentResidenceNeeds,
    deviceInfo,
    myProductsInfo,
    productOfferingsToBuyInfo,
    clientAddresses,
  } = apiNeeds;


  const offerInfo = yield select(offerStore);

  let { parentProductId } = offerInfo;

  const {
    allPOsToBuy,
    productOfferingsToUpgrade,
    productOfferingsToDowngrade,
    netflixToDowngrade,
    netflixObj,
  } = offerInfo;

  let { productId } = offerInfo;

  if (apiNeeds.cqId) {
    return null;
  }

  const { showError, normalizeHttpErrors } = params;

  const reduxUpdate = {
    successUpdate: apisNeedsActions.setCqId,
    errorUpdate: showError,
  };

  const { handledErrors } = yield select(getErrorManagement);
  const { localizedStrings } = yield select(localizedStringsStore);

  if (flowJson.autoSelectProductId
    && myProductsInfo
    && myProductsInfo.clientCategory
    && myProductsInfo.clientCategory.products
  ) {
    const product = myProductsInfo.clientCategory.products.find(element => element.productType === flowJson.autoSelectProductId);

    if (product) {
      yield put(uiNeedsActions.setShowInstallationStep(false));
      if (myProductsInfo.newAddressesArray
        && Array.isArray(myProductsInfo.newAddressesArray)
        && myProductsInfo.newAddressesArray.length === 1
      ) {
        yield put(summaryActions.setInstallationAddress(myProductsInfo.newAddressesArray[0]));
      }
      ({ productId } = product);
    } else if (!installationAddress || !installationAddress.locationId) {
      yield put(uiNeedsActions.setShowInstallationStep(true));
      return megaMinderStatus.OK;
    }
  }

  let parentProductIdTV = null;

  if (myProductsInfo?.tvProductId) {
    parentProductIdTV = myProductsInfo.tvProductId;
  }

  const initCQTVparams = {
    productOfferingsToUpgrade,
    parentProductIdTV,
    reduxUpdate,
    normalizeHttpErrors,
    showError,
    productIdsToDowngrade: [],
  };

  if (netflixToDowngrade) {
    if (myProductsInfo.tvProducts[netflixToDowngrade] && myProductsInfo?.convergentMainPoId) {
      initCQTVparams.productIdsToDowngrade = [myProductsInfo.tvProducts[netflixToDowngrade]];
      if(netflixToDowngrade===NETFLIX_PO){
        initCQTVparams.parentProductIdTV = myProductsInfo.convergentProductId;
      }
      initCQTVparams.productOfferingsToUpgrade = [];
    
      if (productOfferingsToDowngrade) {
        let tvProductsToDowngrade = [];
        if (myProductsInfo?.tvProducts) {
          productOfferingsToDowngrade.forEach(poId => {
            if (myProductsInfo.tvProducts[poId]) {
              tvProductsToDowngrade.push(myProductsInfo.tvProducts[poId]);
            }
          })
    
          if (tvProductsToDowngrade.length > 0) {
            initCQTVparams.productsToDeactivate = tvProductsToDowngrade;
          }
        }
      }
    }
  } else if (netflixObj?.productOffering && !myProductsInfo?.netflixPoId) {
    initCQTVparams.productOfferingsToUpgrade = [netflixObj.productOffering];
    if(netflixToDowngrade===NETFLIX_PO){
      initCQTVparams.parentProductIdTV = myProductsInfo.convergentProductId;
    }
    
    initCQTVparams.productOfferingsToDowngrade = [];

    if (productOfferingsToUpgrade) {
      initCQTVparams.productOfferingsToActivate = productOfferingsToUpgrade;
    }
  
    if (productOfferingsToDowngrade) {
      let tvProductsToDowngrade = [];
      if (myProductsInfo?.tvProducts) {
        productOfferingsToDowngrade.forEach(poId => {
          if (myProductsInfo.tvProducts[poId]) {
            tvProductsToDowngrade.push(myProductsInfo.tvProducts[poId]);
          }
        })
  
        if (tvProductsToDowngrade.length > 0) {
          initCQTVparams.productsToDeactivate = tvProductsToDowngrade;
        }
      }
    }
  }else {
    if (!initCQTVparams.productOfferingsToUpgrade) {
      initCQTVparams.productOfferingsToUpgrade = [];
    }
  
    // pos 166 3872
    if (productOfferingsToDowngrade) {
      let tvProductsToDowngrade = [];
      if (myProductsInfo?.tvProducts) {
        productOfferingsToDowngrade.forEach(poId => {
          if (myProductsInfo.tvProducts[poId]) {
            tvProductsToDowngrade.push(myProductsInfo.tvProducts[poId]);
          }
        })
  
        if (tvProductsToDowngrade.length > 0) {
          initCQTVparams.productIdsToDowngrade = tvProductsToDowngrade;
        }
      }
    }
    yield put(offerActions.cleanPOsToDowngradeNUpgrade());
  }

  if((!initCQTVparams.productIdsToDowngrade || initCQTVparams.productIdsToDowngrade?.length === 0) && (!initCQTVparams.productOfferingsToUpgrade || initCQTVparams.productOfferingsToUpgrade?.length === 0)){
    yield put(showError(handledErrors.BUSINESS_ERRORS.BUY_ALREADY_DONE));
    return null;
  }

  yield put(
    loaderControlActions.setLoaderState({
      loading: true,
      loadingText: localizedStrings[STRING_KEYS.waitingPageTexts.initCQ],
    }),
  );

  let response = null;
  try {
    response = yield call(initNExtendCQTV, initCQTVparams);
  } catch (error) {
    console.log(error);
    return null;
  }
  
  let newAllPOsToBuy = [...allPOsToBuy];
  if(productOfferingsToUpgrade){
    newAllPOsToBuy = [...allPOsToBuy, ...productOfferingsToUpgrade];
  }
  let alias = generateAliasSellingProduct(productOfferingsToBuyInfo, newAllPOsToBuy, isSelfEmployedFlow);

  if (alias) {
    yield put(taggingActions.setAliasSellingProduct(alias));
  }
  if(productOfferingsToDowngrade || netflixToDowngrade){
    let productOfferingsToTag = []
    if(productOfferingsToDowngrade){
      productOfferingsToTag = [...productOfferingsToTag, ...productOfferingsToDowngrade];
    }
    if(netflixToDowngrade){
      productOfferingsToTag = [...productOfferingsToTag, netflixToDowngrade];
    }
    let downgradesAlias = generateAliasSellingProduct(productOfferingsToBuyInfo, productOfferingsToTag, isSelfEmployedFlow);
  
    if (downgradesAlias) {
      yield put(taggingActions.setDowngradesAlias(downgradesAlias));
    }
  }

  if (flowJson.retargeting
  ) {
    yield spawn(sendRetargeting, { showError, normalizeHttpErrors });
  }

  yield call(getFeatures, params);
  // yield call(selectElements, { showError, normalizeHttpErrors });
  // yield call(addToCart, { showError, normalizeHttpErrors });

  return response;
}

export function* extendCQTV(params) {
  const apiNeeds = yield select(apisNeedsStore);
  const flowInfo = yield select(flowStore);

  const { flowJson } = flowInfo;

  const {
    cqId,
    myProductsInfo,
  } = apiNeeds;

  const {
    showError,
    normalizeHttpErrors,
    productOfferingsToUpgrade,
    productOfferingsToDowngrade,
  } = params;

  const reduxUpdate = {
    successUpdate: commonActions.setSelectElementsNeeds,
    errorUpdate: showError,
  };

  const { localizedStrings } = yield select(localizedStringsStore);

  let parentProductIdTV = null;

  if (myProductsInfo?.tvProductId && flowJson.isAddonFlow) {
    parentProductIdTV = myProductsInfo.tvProductId;
  }

  const extendCQTVparams = {
    productOfferingsToUpgrade,
    cqId,
    reduxUpdate,
    normalizeHttpErrors,
    showError,
    parentProductIdTV,
  };

  if (productOfferingsToDowngrade) {
    let tvProductsToDowngrade = [];
    if (myProductsInfo?.tvProducts) {
      productOfferingsToDowngrade.forEach(poId => {
        if (myProductsInfo.tvProducts[poId]) {
          tvProductsToDowngrade.push(myProductsInfo.tvProducts[poId]);
        }
      })

      if (tvProductsToDowngrade.length > 0) {
        extendCQTVparams.productIdsToDowngrade = tvProductsToDowngrade;
      }
    }
  }
  yield put(
    loaderControlActions.setLoaderState({
      loading: true,
      loadingText: localizedStrings[STRING_KEYS.waitingPageTexts.initCQ],
    }),
  );

  yield put(offerActions.cleanPOsToDowngradeNUpgrade());

  let response = null;
  try {
    response = yield call(extendCQTVBit, extendCQTVparams);
    yield call(getFeatures, { showError, normalizeHttpErrors });
  } catch (error) {
    console.log(error);
    return null;
  }

  return response;
}
